/**
 * Damas brasileiras (8x8, 12 pedras por lado).
 *
 * Regras implementadas:
 *   - pedra anda uma casa na diagonal para a frente;
 *   - pedra captura nas quatro diagonais, para a frente e para tras;
 *   - dama anda e captura a distancia ("dama voadora");
 *   - captura e obrigatoria;
 *   - lei da maioria: entre as sequencias possiveis, obriga a de maior
 *     quantidade de pecas capturadas (no jogo brasileiro conta so a
 *     quantidade, nao a qualidade);
 *   - peca capturada so sai do tabuleiro no fim da sequencia, e nao pode ser
 *     saltada duas vezes;
 *   - pedra que chega a ultima linha no meio de uma captura continua como
 *     pedra e nao promove; promove apenas se a sequencia terminar ali;
 *   - quem nao tem lance legal perde.
 *
 * Notacao: casas escuras numeradas de 1 a 32, da esquerda para a direita e
 * de cima para baixo. Lance simples "23-18"; captura "23x18", com as casas
 * de pouso intermediarias listadas em sequencias multiplas: "23x18x9".
 */
import { type GameRules, IllegalMoveError, type Move, type Outcome, type Seat } from './types.ts';

export type Piece = { readonly seat: Seat; readonly king: boolean };
export type Cell = Piece | null;

export interface CheckersState {
  /** 64 casas, indice = linha * 8 + coluna. Linha 0 e o campo do assento 1. */
  readonly board: readonly Cell[];
  readonly turn: Seat;
  /**
   * Lances consecutivos de dama sem captura e sem lance de pedra.
   * Empate em 40 (20 de cada lado).
   */
  readonly idlePlies: number;
}

const SIZE = 8;
export const IDLE_PLIES_DRAW = 40;

const DIRECTIONS: readonly (readonly [number, number])[] = [
  [-1, -1],
  [-1, 1],
  [1, -1],
  [1, 1],
];

/** O assento 0 (brancas) avanca para linhas menores. */
function forwardDelta(seat: Seat): number {
  return seat === 0 ? -1 : 1;
}

function promotionRow(seat: Seat): number {
  return seat === 0 ? 0 : SIZE - 1;
}

function inside(row: number, col: number): boolean {
  return row >= 0 && row < SIZE && col >= 0 && col < SIZE;
}

function isDark(row: number, col: number): boolean {
  return (row + col) % 2 === 1;
}

const idx = (row: number, col: number): number => row * SIZE + col;

// ---------------------------------------------------------------------
// Numeracao 1..32 das casas jogaveis
// ---------------------------------------------------------------------
const SQUARE_TO_INDEX: number[] = [];
const INDEX_TO_SQUARE = new Map<number, number>();
for (let row = 0; row < SIZE; row += 1) {
  for (let col = 0; col < SIZE; col += 1) {
    if (isDark(row, col)) {
      SQUARE_TO_INDEX.push(idx(row, col));
      INDEX_TO_SQUARE.set(idx(row, col), SQUARE_TO_INDEX.length);
    }
  }
}

export function squareToIndex(square: number): number {
  const i = SQUARE_TO_INDEX[square - 1];
  if (i === undefined) throw new Error(`casa fora do tabuleiro: ${square}`);
  return i;
}

export function indexToSquare(index: number): number {
  const s = INDEX_TO_SQUARE.get(index);
  if (s === undefined) throw new Error(`indice nao jogavel: ${index}`);
  return s;
}

// ---------------------------------------------------------------------
// Geracao de lances
// ---------------------------------------------------------------------
interface Generated {
  readonly move: Move;
  readonly board: readonly Cell[];
  readonly captured: number;
  /** true se a sequencia envolveu captura ou lance de pedra. */
  readonly resetsIdle: boolean;
}

/** Trilha de uma sequencia de captura em construcao. */
interface Path {
  readonly landings: number[];
  readonly capturedIndexes: number[];
}

function captureSequences(
  board: Cell[],
  from: number,
  piece: Piece,
  path: Path,
  out: Path[],
): void {
  const row = Math.floor(from / SIZE);
  const col = from % SIZE;
  let extended = false;

  for (const [dr, dc] of DIRECTIONS) {
    if (piece.king) {
      // Dama: procura a primeira peca na diagonal.
      let r = row + dr;
      let c = col + dc;
      while (inside(r, c) && board[idx(r, c)] === null) {
        r += dr;
        c += dc;
      }
      if (!inside(r, c)) continue;

      const victimIndex = idx(r, c);
      const victim = board[victimIndex];
      // Peca propria, ou peca ja capturada nesta sequencia: bloqueia.
      if (!victim || victim.seat === piece.seat) continue;
      if (path.capturedIndexes.includes(victimIndex)) continue;

      // Pousa em qualquer casa vazia depois da vitima.
      let lr = r + dr;
      let lc = c + dc;
      while (inside(lr, lc) && board[idx(lr, lc)] === null) {
        const landing = idx(lr, lc);
        extended = true;
        board[from] = null;
        board[landing] = piece;
        captureSequences(
          board,
          landing,
          piece,
          {
            landings: [...path.landings, landing],
            capturedIndexes: [...path.capturedIndexes, victimIndex],
          },
          out,
        );
        board[landing] = null;
        board[from] = piece;
        lr += dr;
        lc += dc;
      }
    } else {
      // Pedra: salta a peca adjacente nas quatro direcoes.
      const vr = row + dr;
      const vc = col + dc;
      const lr = row + 2 * dr;
      const lc = col + 2 * dc;
      if (!inside(lr, lc)) continue;

      const victimIndex = idx(vr, vc);
      const victim = board[victimIndex];
      if (!victim || victim.seat === piece.seat) continue;
      if (path.capturedIndexes.includes(victimIndex)) continue;

      const landing = idx(lr, lc);
      if (board[landing] !== null) continue;

      extended = true;
      board[from] = null;
      board[landing] = piece;
      captureSequences(
        board,
        landing,
        piece,
        {
          landings: [...path.landings, landing],
          capturedIndexes: [...path.capturedIndexes, victimIndex],
        },
        out,
      );
      board[landing] = null;
      board[from] = piece;
    }
  }

  // Sequencia terminou aqui.
  if (!extended && path.landings.length > 0) out.push(path);
}

function quietMoves(board: readonly Cell[], from: number, piece: Piece): Generated[] {
  const row = Math.floor(from / SIZE);
  const col = from % SIZE;
  const moves: Generated[] = [];

  const directions = piece.king
    ? DIRECTIONS
    : DIRECTIONS.filter(([dr]) => dr === forwardDelta(piece.seat));

  for (const [dr, dc] of directions) {
    let r = row + dr;
    let c = col + dc;
    while (inside(r, c) && board[idx(r, c)] === null) {
      const landing = idx(r, c);
      const next = [...board];
      next[from] = null;
      const promotes = !piece.king && r === promotionRow(piece.seat);
      next[landing] = promotes ? { seat: piece.seat, king: true } : piece;
      moves.push({
        move: `${indexToSquare(from)}-${indexToSquare(landing)}`,
        board: next,
        captured: 0,
        resetsIdle: !piece.king,
      });
      if (!piece.king) break; // pedra anda so uma casa
      r += dr;
      c += dc;
    }
  }
  return moves;
}

function generate(state: CheckersState): Generated[] {
  const working: Cell[] = [...state.board];
  const captures: Generated[] = [];

  for (let i = 0; i < working.length; i += 1) {
    const piece = working[i];
    if (!piece || piece.seat !== state.turn) continue;

    const paths: Path[] = [];
    captureSequences(working, i, piece, { landings: [], capturedIndexes: [] }, paths);

    for (const path of paths) {
      const next: Cell[] = [...state.board];
      next[i] = null;
      for (const captured of path.capturedIndexes) next[captured] = null;

      const landing = path.landings[path.landings.length - 1] as number;
      const landingRow = Math.floor(landing / SIZE);
      // Promove apenas se a sequencia TERMINA na ultima linha.
      const promotes = !piece.king && landingRow === promotionRow(piece.seat);
      next[landing] = promotes ? { seat: piece.seat, king: true } : piece;

      captures.push({
        move: [i, ...path.landings].map(indexToSquare).join('x'),
        board: next,
        captured: path.capturedIndexes.length,
        resetsIdle: true,
      });
    }
  }

  if (captures.length > 0) {
    // Lei da maioria: so as sequencias de captura maxima sao legais.
    const best = Math.max(...captures.map((c) => c.captured));
    return captures.filter((c) => c.captured === best);
  }

  const quiet: Generated[] = [];
  for (let i = 0; i < working.length; i += 1) {
    const piece = working[i];
    if (!piece || piece.seat !== state.turn) continue;
    quiet.push(...quietMoves(state.board, i, piece));
  }
  return quiet;
}

// ---------------------------------------------------------------------
// Regras
// ---------------------------------------------------------------------
function initialBoard(): Cell[] {
  const board: Cell[] = Array<Cell>(SIZE * SIZE).fill(null);
  for (let row = 0; row < SIZE; row += 1) {
    for (let col = 0; col < SIZE; col += 1) {
      if (!isDark(row, col)) continue;
      if (row < 3) board[idx(row, col)] = { seat: 1, king: false };
      if (row > 4) board[idx(row, col)] = { seat: 0, king: false };
    }
  }
  return board;
}

const PIECE_CHARS: Record<string, Piece> = {
  w: { seat: 0, king: false },
  W: { seat: 0, king: true },
  b: { seat: 1, king: false },
  B: { seat: 1, king: true },
};

function pieceToChar(cell: Cell): string {
  if (!cell) return '.';
  if (cell.seat === 0) return cell.king ? 'W' : 'w';
  return cell.king ? 'B' : 'b';
}

export const checkers: GameRules<CheckersState> = {
  id: 'CHECKERS',

  initial: () => ({ board: initialBoard(), turn: 0, idlePlies: 0 }),

  turn: (state) => state.turn,

  legalMoves: (state) => generate(state).map((g) => g.move),

  apply(state, move) {
    if (checkers.outcome(state) !== null) {
      throw new IllegalMoveError(move, 'partida ja terminada');
    }
    const found = generate(state).find((g) => g.move === move);
    if (!found) {
      // Distingue lance inexistente de lance legal-mas-proibido pela lei
      // da maioria: a mensagem muda o suporte que o jogador recebe.
      const anyCapture = generate({ ...state, idlePlies: 0 }).some((g) => g.captured > 0);
      throw new IllegalMoveError(
        move,
        anyCapture ? 'captura obrigatoria de maioria nao respeitada' : 'lance nao disponivel',
      );
    }
    return {
      board: found.board,
      turn: state.turn === 0 ? 1 : 0,
      idlePlies: found.resetsIdle ? 0 : state.idlePlies + 1,
    };
  },

  outcome(state): Outcome | null {
    const hasOwn = state.board.some((c) => c?.seat === state.turn);
    if (!hasOwn) {
      return { kind: 'WIN', seat: state.turn === 0 ? 1 : 0, reason: 'SEM_PECAS' };
    }
    if (generate(state).length === 0) {
      return { kind: 'WIN', seat: state.turn === 0 ? 1 : 0, reason: 'SEM_LANCE_LEGAL' };
    }
    if (state.idlePlies >= IDLE_PLIES_DRAW) {
      return { kind: 'DRAW', reason: 'VINTE_LANCES_SEM_PROGRESSO' };
    }
    return null;
  },

  serialize: (state) =>
    `${state.board.map(pieceToChar).join('')}|${state.turn}|${state.idlePlies}`,

  deserialize(text) {
    const [cells, turn, idle] = text.split('|');
    if (cells?.length !== SIZE * SIZE || (turn !== '0' && turn !== '1')) {
      throw new Error(`estado invalido: ${text}`);
    }
    return {
      board: [...cells].map((ch) => (ch === '.' ? null : (PIECE_CHARS[ch] ?? null))),
      turn: Number(turn) as Seat,
      idlePlies: Number(idle ?? 0),
    };
  },
};
